import { t } from 'i18next';
import capitalize from 'lodash/capitalize';
import uniq from 'lodash/uniq';
import { DateTime } from 'luxon';
import {
  Day,
  ScheduleTimeBlock,
  ScheduleTimeBlockInput,
  TimeBlockCategory,
  TimeBlockType,
} from '~graphql/__generated__/types';
import { DAYS_OF_WEEK_MON_FIRST, HOUR_REGEX } from '~utils/timeBlocks';

/**
 * A DEFAULT_INPUT_SOURCE block always plays the display's own default input, so the
 * source is fixed rather than chosen - the same value the powerSchedule bridge writes.
 */
export const DEFAULT_INPUT_SOURCE_VALUE = 'DEFAULT';

export const MAX_TIME_SLOTS_PER_DAY = 35;
export const MAX_UNIQUE_PLAYLISTS_PER_SCHEDULE = 5;
/** Depth of the stack: a day may not hold more than this many overlapping slots. */
export const MAX_PRIORITY_LANES = 5;
export const MIN_SLOT_DURATION_MINUTES = 15;
/** A slot cannot span more than one day, so 24h is the ceiling. */
export const MAX_SLOT_DURATION_MINUTES = 24 * 60;

const TIME_BLOCK_TYPE_CATEGORY: Record<TimeBlockType, TimeBlockCategory> = {
  [TimeBlockType.Playlist]: TimeBlockCategory.Content,
  [TimeBlockType.Apps]: TimeBlockCategory.Content,
  [TimeBlockType.Bookmark]: TimeBlockCategory.Content,
  [TimeBlockType.Source]: TimeBlockCategory.Content,
  [TimeBlockType.DefaultInputSource]: TimeBlockCategory.Content,
  [TimeBlockType.Brightness]: TimeBlockCategory.Settings,
};

export function getTimeBlockCategory(type: TimeBlockType): TimeBlockCategory {
  return TIME_BLOCK_TYPE_CATEGORY[type];
}

export const CONTENT_TIME_BLOCK_TYPES = [
  TimeBlockType.DefaultInputSource,
  TimeBlockType.Apps,
  TimeBlockType.Bookmark,
  TimeBlockType.Source,
  TimeBlockType.Playlist,
];

export const SETTINGS_TIME_BLOCK_TYPES = [TimeBlockType.Brightness];

/**
 * A validation failure the slot form shows as a titled error alert: `title` is the
 * bold heading, `message` the sentence under it.
 */
export class ScheduleValidationError extends Error {
  constructor(
    public readonly title: string,
    message: string,
    public readonly severity: 'error' | 'warning' = 'error',
  ) {
    super(message);
    this.name = 'ScheduleValidationError';
  }
}

export function timeBlockTypesForCategory(category: TimeBlockCategory): TimeBlockType[] {
  return category === TimeBlockCategory.Content
    ? CONTENT_TIME_BLOCK_TYPES
    : SETTINGS_TIME_BLOCK_TYPES;
}

/**
 * The end a slot needs when its start is moved past it: an hour later, or 23:59
 * once an hour would run past the end of the day. Lets a start move freely instead of
 * being rejected for crossing the end that is still in place.
 */
export const endAfterStart = (start: string): string => {
  const [hour, minute] = start.split(':').map(Number);

  return hour * 100 + minute <= 2300
    ? DateTime.fromObject({ hour, minute }).plus({ hours: 1 }).toFormat('HH:mm')
    : '23:59';
};

export const toMinutes = (time: string, property: 'start' | 'end'): number => {
  if (property === 'end' && time === '00:00') return 24 * 60;

  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Strips the server-only fields off a block so it can be handed straight back to
 * `scheduleUpdate`, which takes `ScheduleTimeBlockInput` (no `category`, no `__typename`).
 */
export const toScheduleTimeBlockInput = (
  block: Pick<ScheduleTimeBlock, 'day' | 'start' | 'end' | 'type' | 'priority' | 'data'>,
): ScheduleTimeBlockInput => ({
  day: block.day,
  start: block.start,
  end: block.end,
  type: block.type,
  priority: block.priority,
  data: {
    applicationId: block.data.applicationId,
    id: block.data.id,
    mediaType: block.data.mediaType,
    source: block.data.source,
    url: block.data.url,
    value: block.data.value,
  },
});

/**
 * True once the block carries the payload its type needs. Mirrors the guard in the
 * single-slot form, so the stacked editor refuses to save what the mutation would
 * reject.
 */
export const hasCompleteTimeBlockData = (block: ScheduleTimeBlockInput): boolean => {
  switch (block.type) {
    case TimeBlockType.Playlist:
      return !!block.data.id;
    case TimeBlockType.Apps:
      return !!block.data.applicationId?.trim();
    case TimeBlockType.Bookmark:
      return !!block.data.url?.trim();
    case TimeBlockType.Source:
      return !!block.data.source?.trim();
    case TimeBlockType.DefaultInputSource:
      return true;
    case TimeBlockType.Brightness:
      return block.data.value != null;
    default:
      return false;
  }
};

/** True once both ends of the slot hold a whole hh:mm time. */
export const hasCompleteTimeBlockTimes = (block: ScheduleTimeBlockInput): boolean =>
  HOUR_REGEX.test(block.start) && HOUR_REGEX.test(block.end);

const blocksOverlap = (a: ScheduleTimeBlockInput, b: ScheduleTimeBlockInput) =>
  toMinutes(a.start, 'start') < toMinutes(b.end, 'end') &&
  toMinutes(b.start, 'start') < toMinutes(a.end, 'end');

/**
 * Priorities are assigned bottom-up: the stack rests on the bottom lane and grows
 * upwards. A slot that overlaps nothing takes the bottom lane; one that covers a slot
 * already placed sits directly above it. So the first slot of a day lands in lane 5,
 * a second slot at the same time lands in lane 4, and a slot at a different time
 * starts again at lane 5.
 *
 * Of the slots covering a given minute the lowest-numbered one plays, so the newest
 * slot still wins wherever it reaches, whatever its type.
 *
 * Recency is read off the current priority - stored slots run 5 (oldest) upwards, and
 * a block being added arrives at 0, which sorts last so it stacks on top of what it
 * covers. Removing a slot closes the gap it leaves, because the survivors are
 * renumbered from scratch.
 */
export const assignTimeBlockPriorities = (
  timeBlocks: ScheduleTimeBlockInput[],
): ScheduleTimeBlockInput[] => {
  const groups = new Map<string, ScheduleTimeBlockInput[]>();
  for (const block of timeBlocks) {
    const key = [block.day, getTimeBlockCategory(block.type)].join('|');
    groups.set(key, [...(groups.get(key) ?? []), block]);
  }

  const priorities = new Map<ScheduleTimeBlockInput, number>();
  for (const group of groups.values()) {
    // Lanes count down from the bottom, so the oldest slot carries the highest
    // number. A block being added arrives at 0 and sorts last, on top of the rest.
    const oldestFirst = [...group].sort((a, b) => b.priority - a.priority);
    const placed: ScheduleTimeBlockInput[] = [];

    for (const block of oldestFirst) {
      const covered = placed.filter((other) => blocksOverlap(other, block));
      priorities.set(
        block,
        covered.length
          ? Math.min(...covered.map((other) => priorities.get(other) ?? MAX_PRIORITY_LANES)) - 1
          : MAX_PRIORITY_LANES,
      );
      placed.push(block);
    }
  }

  return timeBlocks.map((block) => ({
    ...block,
    priority: priorities.get(block) ?? block.priority,
  }));
};

/**
 * Blocks live in lanes of day + priority + category. Blocks in different lanes may
 * overlap, blocks within one lane may not. Mirrors `validateAndSortTypedTimeBlocks`
 * on the API so the portal rejects exactly what the mutation would reject.
 */
const laneOf = (block: ScheduleTimeBlockInput) =>
  [block.day, block.priority, getTimeBlockCategory(block.type)].join('|');

export const validateAndSortScheduleTimeBlocks = (
  timeBlocks: ScheduleTimeBlockInput[],
  options?: { useShortErrorMessage?: boolean; throwError?: boolean },
): {
  validatedTimeBlocks: ScheduleTimeBlockInput[];
  conflictingTimeBlocks: ScheduleTimeBlockInput[];
} => {
  const { useShortErrorMessage = false, throwError = true } = options || {};

  const blocks = timeBlocks.filter(Boolean);

  const blocksPerDay = new Map<Day, number>();
  for (const block of blocks) {
    const count = (blocksPerDay.get(block.day) ?? 0) + 1;

    if (count > MAX_TIME_SLOTS_PER_DAY) {
      throw new ScheduleValidationError(
        t('scheduleSlotErrMaxSlotsTitle'),
        t('scheduleSlotErrMaxSlotsDesc', { limit: MAX_TIME_SLOTS_PER_DAY }),
      );
    }

    blocksPerDay.set(block.day, count);
  }

  const uniquePlaylistIds = new Set(
    blocks.flatMap((block) =>
      block.type === TimeBlockType.Playlist && block.data.id ? [block.data.id] : [],
    ),
  );
  if (uniquePlaylistIds.size > MAX_UNIQUE_PLAYLISTS_PER_SCHEDULE) {
    throw new ScheduleValidationError(
      '',
      t('scheduleSlotErrMaxPlaylists', { limit: MAX_UNIQUE_PLAYLISTS_PER_SCHEDULE }),
      'warning',
    );
  }

  for (const block of blocks) {
    // Assignment counts down from the bottom lane, so a stack deeper than the
    // available lanes lands on 0 or below.
    if (block.priority < 1 || block.priority > MAX_PRIORITY_LANES) {
      throw new ScheduleValidationError(
        t('scheduleSlotErrMaxLanesTitle'),
        t('scheduleSlotErrMaxLanesDesc', { limit: MAX_PRIORITY_LANES }),
      );
    }
  }

  for (const block of blocks) {
    const duration = toMinutes(block.end, 'end') - toMinutes(block.start, 'start');

    if (duration <= 0) {
      throw new ScheduleValidationError(
        t('scheduleSlotErrInvalidRangeTitle'),
        t('scheduleSlotErrInvalidRangeDesc', { start: block.start, end: block.end }),
      );
    }

    if (duration < MIN_SLOT_DURATION_MINUTES) {
      throw new ScheduleValidationError(
        t('scheduleSlotErrTooShortTitle'),
        t('scheduleSlotErrTooShortDesc', {
          minutes: MIN_SLOT_DURATION_MINUTES,
          start: block.start,
          end: block.end,
          duration,
        }),
      );
    }

    if (duration > MAX_SLOT_DURATION_MINUTES) {
      throw new ScheduleValidationError(
        t('scheduleSlotErrTooLongTitle'),
        t('scheduleSlotErrTooLongDesc', { hours: MAX_SLOT_DURATION_MINUTES / 60 }),
      );
    }
  }

  const sorted = [...blocks].sort(
    (a, b) =>
      DAYS_OF_WEEK_MON_FIRST.indexOf(a.day) - DAYS_OF_WEEK_MON_FIRST.indexOf(b.day) ||
      a.priority - b.priority ||
      toMinutes(a.start, 'start') - toMinutes(b.start, 'start'),
  );

  const lanes = new Map<string, ScheduleTimeBlockInput[]>();
  for (const block of sorted) {
    const lane = lanes.get(laneOf(block)) ?? [];
    lane.push(block);
    lanes.set(laneOf(block), lane);
  }

  const conflictingTimeBlocks = [...lanes.values()].flatMap((lane) =>
    lane.filter(
      (block, i, arr) =>
        i !== 0 && toMinutes(block.start, 'start') < toMinutes(arr[i - 1].end, 'end'),
    ),
  );

  if (throwError && conflictingTimeBlocks.length) {
    throw new ScheduleValidationError(
      t('scheduleSlotErrConflictTitle'),
      useShortErrorMessage
        ? t('scheduleSlotErrConflictDescShort')
        : t('scheduleSlotErrConflictDesc', {
            days: uniq(conflictingTimeBlocks.map(({ day }) => capitalize(day)))
              .join(', ')
              .replace(/,(?!.*,)/gim, ' and'),
          }),
    );
  }

  return { validatedTimeBlocks: sorted, conflictingTimeBlocks };
};
